import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type { PlneniCell, PlneniReport, PlneniRow } from '../api/affilmaster';
import { ServiceBar, type ServiceBarSelect } from '../shell/ServiceBar';
import { useFeather } from '../shell/useFeather';
import { ArcPage, ArcTableFrame } from '../components/arc/primitives/arc-primitives';
import './stranky/stranky.css';

export type PlneniMarket = 'CZ' | 'SK';

interface Props {
  market: PlneniMarket;
  load: (market: PlneniMarket) => Promise<PlneniReport>;
  head?: ReactNode;
  barExtra?: ReactNode;
}

type Metric = 'reg' | 'ftd' | 'dep' | 'clicks';

const METRIC_LABEL: Record<Metric, string> = { reg: 'Registrace', ftd: 'FTD', dep: 'Vklady', clicks: 'Prokliky' };
const CZK_PER_EUR = 25;
const nf = new Intl.NumberFormat('cs-CZ');

function errText(e: unknown): string {
  return e instanceof Error ? e.message : 'Neznámá chyba';
}

const nf2 = new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const NBSP = String.fromCharCode(160);

function fmtDeposit(eur: number, market: PlneniMarket, short = false): string {
  const value = market === 'CZ' ? eur * CZK_PER_EUR : eur;
  if (short) {
    if (value >= 1_000_000) return `${nf2.format(value / 1_000_000)}${NBSP}mil.`;
    if (value >= 10_000) return `${nf.format(Math.round(value / 1000))}${NBSP}tis.`;
    return nf.format(Math.round(value));
  }
  return `${nf.format(Math.round(value))}${NBSP}${market === 'CZ' ? 'Kč' : '€'}`;
}

function fmtMetric(cell: PlneniCell | undefined, metric: Metric, market: PlneniMarket, short = false): string {
  if (!cell) return '–';
  if (metric === 'dep') return fmtDeposit(cell.dep, market, short);
  return nf.format(cell[metric]);
}

function promotes(cell: PlneniCell | undefined): boolean {
  return !!cell && (cell.reg > 0 || cell.ftd > 0 || cell.dep > 0 || cell.clicks > 0);
}

function todo(cell: PlneniCell | undefined, metric: Metric): boolean {
  return !promotes(cell) || !cell || cell[metric] <= 0;
}

function fmtDay(v: string | null): string {
  if (!v) return '';
  const [y, m, d] = v.slice(0, 10).split('-').map(Number);
  return `${d}. ${m}. ${y}`;
}

function emptyCell(): PlneniCell {
  return { reg: 0, ftd: 0, dep: 0, clicks: 0, since: null, last: null };
}

export function AffiliatePlneni({ market, load, head, barExtra }: Props): JSX.Element {
  const [data, setData] = useState<PlneniReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [metric, setMetric] = useState<Metric>('reg');
  const [q, setQ] = useState('');
  useFeather([data, metric, q, market]);

  useEffect(() => {
    let alive = true;
    setData(null);
    setError(null);
    load(market).then((d) => { if (alive) setData(d); }).catch((e) => { if (alive) setError(errText(e)); });
    return () => { alive = false; };
  }, [market, load]);

  const rows = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    return data.rows
      .filter((r) => !needle || r.name.toLowerCase().includes(needle) || (r.company ?? '').toLowerCase().includes(needle) || r.papId.toLowerCase().includes(needle))
      .sort((a, b) => b.total[metric] - a.total[metric] || b.total.reg - a.total.reg || a.name.localeCompare(b.name, 'cs'));
  }, [data, metric, q]);

  const totals = useMemo(() => {
    const perClient = new Map<number, PlneniCell>();
    const promoting = new Map<number, number>();
    const missing = new Map<number, number>();
    const all = emptyCell();
    if (!data) return { perClient, all, promoting, missing };
    for (const c of data.clients) {
      perClient.set(c.partnerId, emptyCell());
      promoting.set(c.partnerId, 0);
      missing.set(c.partnerId, 0);
    }
    for (const r of rows) {
      for (const c of data.clients) {
        const cell = r.cells[String(c.partnerId)];
        if (todo(cell, metric)) missing.set(c.partnerId, (missing.get(c.partnerId) ?? 0) + 1);
        if (!cell) continue;
        const t = perClient.get(c.partnerId)!;
        t.reg += cell.reg; t.ftd += cell.ftd; t.dep += cell.dep; t.clicks += cell.clicks;
        if (promotes(cell)) promoting.set(c.partnerId, (promoting.get(c.partnerId) ?? 0) + 1);
      }
      all.reg += r.total.reg; all.ftd += r.total.ftd; all.dep += r.total.dep; all.clicks += r.total.clicks;
    }
    return { perClient, all, promoting, missing };
  }, [data, rows, metric]);

  const selects: ServiceBarSelect[] = [
    { label: 'Hodnota', value: metric, onChange: (v) => setMetric(v as Metric), options: (Object.keys(METRIC_LABEL) as Metric[]).map((m) => ({ value: m, label: m === 'dep' ? `Vklady (${market === 'CZ' ? 'Kč' : '€'})` : METRIC_LABEL[m] })) },
  ];
  const compact = (data?.clients.length ?? 0) > 6;

  return (
    <ArcPage as="div" className="st-b2b st-sk-report">
      <div className="st-sk-head">
        {head}
        <span className="st-sk-legend"><i />k oslovení: partner klienta nepropaguje nebo má nulu</span>
      </div>
      <ServiceBar
        variant="seg"
        selects={selects}
        extra={barExtra}
        search={q}
        onSearch={setQ}
        searchPlaceholder="Hledat partnera"
        onReset={() => { setMetric('reg'); setQ(''); }}
        right={(
          <div className="st-news-service-actions">
            <span className="st-count"><strong>{rows.length}</strong><span>{rows.length === 1 ? 'partner' : rows.length > 1 && rows.length < 5 ? 'partneři' : 'partnerů'}</span></span>
          </div>
        )}
      />
      {error ? <div className="st-empty st-b2b-empty"><strong>Přehled se nepodařilo načíst</strong><small>{error}</small></div>
        : data === null ? <div className="st-empty">Načítám…</div>
          : rows.length === 0 ? <div className="st-empty st-b2b-empty"><strong>Žádní partneři</strong><small>Zkuste upravit hledání.</small></div> : (
            <ArcTableFrame className="st-promo-table-shell st-sk-shell" label={`Plnění ${market} partnerů`}>
              <table className={`table table-sm st-table st-b2b-output-table st-sk-matrix${compact ? ' st-sk-compact' : ''}`} style={compact ? { tableLayout: 'fixed', minWidth: 276 + data.clients.length * 72 } : undefined}>
                {compact ? <colgroup><col style={{ width: 180 }} />{data.clients.map((c) => <col key={c.partnerId} />)}<col style={{ width: 96 }} /></colgroup> : null}
                <thead>
                  <tr>
                    <th className="st-sk-name">Affiliate partner</th>
                    {data.clients.map((c) => (
                      <th key={c.partnerId} className="st-sk-client" title={c.name}>
                        <span className="st-sk-client-head">{c.logo ? <img src={c.logo} alt="" loading="lazy" /> : null}<span>{c.name}</span></span>
                      </th>
                    ))}
                    <th className="st-sk-client st-sk-total">Celkem</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => <Row key={r.papId} row={r} clients={data.clients} metric={metric} market={market} short={compact} />)}
                </tbody>
                <tfoot>
                  <tr>
                    <td className="st-sk-name"><strong>Celkem</strong><small className="d-block text-muted fw-normal">{rows.length} partnerů</small></td>
                    {data.clients.map((c) => (
                      <td key={c.partnerId} className="st-sk-cell on">
                        <strong>{fmtMetric(totals.perClient.get(c.partnerId), metric, market, compact)}</strong>
                        <small>{totals.promoting.get(c.partnerId) ?? 0} propaguje</small>
                        {(totals.missing.get(c.partnerId) ?? 0) > 0 ? <small className="st-sk-missing">{totals.missing.get(c.partnerId)} {compact ? 'oslovit' : 'k oslovení'}</small> : null}
                      </td>
                    ))}
                    <td className="st-sk-cell on st-sk-total"><strong>{fmtMetric(totals.all, metric, market, compact)}</strong></td>
                  </tr>
                </tfoot>
              </table>
            </ArcTableFrame>
          )}
      <p className="st-sk-note">Zdroj: výkonnostní data Affilmasteru za celé období spolupráce. V přehledu jsou jen partneři, kteří u některé {market} kampaně přivedli registraci nebo vklad. Červeně jsou místa k oslovení: partner klienta nepropaguje, nebo u něj má ve zvolené hodnotě nulu. FTD = hráči s prvním vkladem, vklady v {market === 'CZ' ? 'korunách (kurz 25 Kč za euro)' : 'eurech'}.</p>
    </ArcPage>
  );
}

function Row({ row, clients, metric, market, short }: { row: PlneniRow; clients: PlneniReport['clients']; metric: Metric; market: PlneniMarket; short: boolean }): JSX.Element {
  return (
    <tr>
      <td className="st-sk-name" data-label="Affiliate partner" title={[row.name, row.company && row.company !== row.name ? row.company : null].filter(Boolean).join(', ')}>
        <strong>{row.name}</strong>
        <small className="d-block text-muted fw-normal">{[row.company && row.company !== row.name ? row.company : null, row.markets ? `trh ${row.markets}` : null, row.total.since ? `od ${fmtDay(row.total.since)}` : null].filter(Boolean).join(' · ')}</small>
      </td>
      {clients.map((c) => {
        const cell = row.cells[String(c.partnerId)];
        const on = promotes(cell);
        const miss = todo(cell, metric);
        return (
          <td key={c.partnerId} className={`st-sk-cell ${miss ? 'todo' : 'on'}`} data-label={c.name} title={on && cell ? `${c.name}: ${nf.format(cell.reg)} registrací, ${nf.format(cell.ftd)} FTD, ${fmtDeposit(cell.dep, market)} vkladů, ${nf.format(cell.clicks)} prokliků${cell.last ? `, naposledy ${fmtDay(cell.last)}` : ''}` : `${c.name}: nepropaguje`}>
            {on ? <><strong>{fmtMetric(cell, metric, market, short)}</strong>{metric !== 'ftd' && cell && cell.ftd > 0 ? <small>{nf.format(cell.ftd)} FTD</small> : miss ? <small>{short ? 'oslovit' : 'k oslovení'}</small> : null}</> : <><strong>–</strong><small>{short ? 'oslovit' : 'nepropaguje'}</small></>}
          </td>
        );
      })}
      <td className="st-sk-cell st-sk-total" data-label="Celkem"><strong>{fmtMetric(row.total, metric, market, short)}</strong>{metric !== 'ftd' && row.total.ftd > 0 ? <small>{nf.format(row.total.ftd)} FTD</small> : null}</td>
    </tr>
  );
}
